import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { UploadCloud, Zap, Loader2, ArrowRight, FileCheck2, Gauge, Truck, Car } from "lucide-react";
import { useI18n } from "@/i18n";
import { svcName } from "@/i18n/catalog";

const GROUP_COLORS = { Performance: "#00E5FF", Émissions: "#8B5CF6", Diagnostic: "#FFB020", Sécurité: "#00E5FF" };

export const QuoteWidget = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { t } = useI18n();
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [vehicleClass, setVehicleClass] = useState("car");
  const isTruck = vehicleClass === "truck_agri";

  useEffect(() => { api.get("/catalog", { params: { vehicle_class: vehicleClass } }).then(({ data }) => setServices(data.services)); }, [vehicleClass]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const getQuote = async () => {
    if (!file || selected.length === 0) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("services", JSON.stringify(selected));
      fd.append("vehicle_class", vehicleClass);
      const { data } = await api.post("/public/quote", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };

  return (
    <section id="quote" className="px-6 md:px-12 py-20 border-t border-white/5">
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">{t("quote.kicker")}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">{t("quote.title")}</h2>
          <p className="mt-4 text-zinc-400 max-w-md text-sm leading-relaxed">
            {t("quote.subtitle")}
          </p>

          <div className="mt-6 flex gap-2" data-testid="quote-class-tabs">
            {[["car", t("vc.car"), Car], ["truck_agri", t("vc.truck"), Truck]].map(([id, label, Ico]) => (
              <button key={id} data-testid={`quote-class-${id}`} onClick={() => setVehicleClass(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm font-display font-bold uppercase tracking-wider text-xs transition-colors border ${vehicleClass === id ? (id === "truck_agri" ? "bg-[#FFB020] text-black border-[#FFB020]" : "bg-aurora-cyan text-black border-aurora-cyan") : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
                <Ico className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            data-testid="quote-upload"
            className={`mt-4 cursor-pointer glass rounded-md p-6 border-2 border-dashed transition-colors text-center ${isTruck ? "border-[#FFB020]/60 hover:border-[#FFB020]" : "border-zinc-700 hover:border-aurora-cyan/50"}`}
          >
            <input ref={fileRef} type="file" className="hidden" data-testid="quote-file-input" onChange={(e) => setFile(e.target.files[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-2"><FileCheck2 className="w-6 h-6 text-signal-success" /><span className="font-mono text-sm">{file.name}</span></div>
            ) : (
              <><UploadCloud className="w-8 h-8 text-aurora-cyan mx-auto mb-2" /><span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">{t("quote.dropFile")}</span></>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {services.map((s) => {
              const on = selected.includes(s.id);
              return (
                <button key={s.id} data-testid={`quote-svc-${s.id}`} onClick={() => toggle(s.id)} className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`} style={on ? {} : { borderColor: `${GROUP_COLORS[s.group]}30` }}>
                  {svcName(s, t)}
                </button>
              );
            })}
          </div>

          <button onClick={getQuote} disabled={loading || !file || selected.length === 0} data-testid="get-quote-btn" className="mt-6 flex items-center gap-2 px-6 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />} {t("quote.getQuote")}
          </button>
        </div>

        {/* Result */}
        <div className="lg:pt-16">
          {result ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-md p-7" data-testid="quote-result">
              {result.instant && (
                <div className="mb-4 flex items-center gap-2 p-3 rounded-sm bg-signal-success/10 border border-signal-success/40">
                  <Zap className="w-5 h-5 text-signal-success" />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-signal-success">{t("quote.instantAvail")}</span>
                </div>
              )}
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t("quote.estimation")}</div>
                  <div className="font-display text-5xl font-bold text-aurora-cyan">{result.total_credits}<span className="text-lg text-zinc-500 ml-1">{t("quote.credits")}</span></div>
                  <div className="font-mono text-xs text-zinc-400 mt-1">{t("quote.approx")} {result.est_eur}€</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t("quote.delay")}</div>
                  <div className="font-display text-xl text-white">{result.delay}</div>
                </div>
              </div>
              <div className="space-y-2 mb-5">
                {result.items.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm border-b border-white/5 pb-2">
                    <span className="text-zinc-300">{svcName(it, t)}</span>
                    <span className="font-mono text-aurora-cyan">{it.credits} cr.</span>
                  </div>
                ))}
              </div>
              <div className="font-mono text-[10px] text-zinc-500 mb-5">{t("quote.ecuDetected")} : <span className="text-zinc-300">{result.family || t("quote.notId")}</span> · {result.size} {t("quote.bytes")}</div>
              <button onClick={() => navigate("/login")} data-testid="quote-cta-btn" className="group w-full flex items-center justify-center gap-2 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors shadow-glow">
                {t("quote.createAccount")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ) : (
            <div className="glass rounded-md p-7 border-dashed border border-white/10 text-center">
              <Gauge className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">{t("quote.placeholder")}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
